import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'

// Layout
import Layout from './components/Layout/Layout'

// Contexto de autenticación
import { AuthProvider, useAuth } from './context/AuthContext'

// Login se carga de forma síncrona: es la primera pantalla que ve el usuario.
import Login from './pages/Login'

/**
 * El resto de páginas se cargan bajo demanda (code splitting).
 * Evita descargar las 12 pantallas —y sus dependencias— en la carga inicial,
 * que es lo que más pesa en conexiones móviles.
 */
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Empresas = lazy(() => import('./pages/Empresas'))
const Usuarios = lazy(() => import('./pages/Usuarios'))
const Compras = lazy(() => import('./pages/Compras'))
const Ventas = lazy(() => import('./pages/Ventas'))
const GenerarATS = lazy(() => import('./pages/GenerarATS'))
const ImportarXML = lazy(() => import('./pages/ImportarXML'))
const ImportarVentasSRIPage = lazy(() => import('./pages/ImportarVentasSRIPage'))
const ImportarATS = lazy(() => import('./pages/ImportarATS'))
const Reportes = lazy(() => import('./pages/Reportes'))
const CodigosRetencion = lazy(() => import('./pages/CodigosRetencion'))

/** Indicador de carga a pantalla completa, usado mientras se resuelve la sesión. */
function PantallaCargando() {
  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label="Cargando"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100dvh',
      }}
    >
      <CircularProgress />
    </Box>
  )
}

/** Indicador de carga dentro del layout, mientras llega el chunk de la página. */
function ContenidoCargando() {
  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label="Cargando contenido"
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}
    >
      <CircularProgress />
    </Box>
  )
}

// Componente de ruta protegida
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth()

  if (loading) {
    return <PantallaCargando />
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return (
    <Layout>
      <Suspense fallback={<ContenidoCargando />}>{children}</Suspense>
    </Layout>
  )
}

function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Ruta pública */}
        <Route path="/login" element={<Login />} />

        {/* Rutas protegidas */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/empresas"
          element={
            <ProtectedRoute>
              <Empresas />
            </ProtectedRoute>
          }
        />
        <Route
          path="/usuarios"
          element={
            <ProtectedRoute>
              <Usuarios />
            </ProtectedRoute>
          }
        />
        <Route
          path="/compras"
          element={
            <ProtectedRoute>
              <Compras />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ventas"
          element={
            <ProtectedRoute>
              <Ventas />
            </ProtectedRoute>
          }
        />
        <Route
          path="/generar-ats"
          element={
            <ProtectedRoute>
              <GenerarATS />
            </ProtectedRoute>
          }
        />
        <Route
          path="/importar-xml"
          element={
            <ProtectedRoute>
              <ImportarXML />
            </ProtectedRoute>
          }
        />
        <Route
          path="/importar-ventas-sri"
          element={
            <ProtectedRoute>
              <ImportarVentasSRIPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reportes"
          element={
            <ProtectedRoute>
              <Reportes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/codigos-retencion"
          element={
            <ProtectedRoute>
              <CodigosRetencion />
            </ProtectedRoute>
          }
        />
        <Route
          path="/importar-ats"
          element={
            <ProtectedRoute>
              <ImportarATS />
            </ProtectedRoute>
          }
        />

        {/* Ruta 404 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  )
}

export default App
